import { memo, useState } from 'react';
import { format, isValid } from 'date-fns';
import { Link2, Plus, X as XIcon, Trash2, Loader2 } from 'lucide-react';
import type { Task, TaskStatus } from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { GanttBar } from './gantt-bar';
import { resolveTaskRange, type GanttGroupBy } from './gantt-config';
import { getStatusLabel } from './gantt-i18n';
import { getSprintColourForTask, type SprintDef } from '@/hooks/use-gantt';
import type { CustomField } from '@/lib/schemas';
import { useTasks } from '@/hooks/use-tasks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useTaskDependencies, useCreateTaskDependency, useDeleteTaskDependency, type TaskDependencyType } from '@/hooks/use-task-dependencies';

// ============================================
// GANTT ROW
// ============================================
//
// t17: one horizontal strip in the Gantt timeline. Stacks a left
// "label" pane (task title + status badge + assignees) with a right
// "timeline" pane (the absolute-positioned bar over the time grid).
//
// The row is fully controlled — the parent GanttTimeline passes the
// bar's left/width in pixels and the visible time scale, so the
// row doesn't need to know about date math.
//
// R9 / t36: `isSubtask`, `subtaskCount`, `isExpanded`, and
// `onToggleExpand` were all removed when the chevron was
// deleted — every row is now a parent row. `isPreviewing`
// still flips a few styles on the bar so the user can tell the
// geometry is live during a drag.

export interface GanttRowProps {
    task: Task;
    /** Pixel offset of the bar's start within the timeline (left edge). */
    barLeft: number;
    /** Pixel width of the bar. >= 0 — a task with no range renders as a dot. */
    barWidth: number;
    /** The visible (concrete) time scale. */
    timeScale: 'day' | 'week' | 'month' | 'auto';
    /** Group setting — affects the status badge colour but not the layout. */
    groupBy: GanttGroupBy;
    canDrag: boolean;
    sprints: SprintDef[];
    sprintFieldId?: string;
    customFields: CustomField[];
    onClick?: (task: Task) => void;
    /** R3 / t17: this bar's geometry is being driven by a live drag preview. */
    isPreviewing?: boolean;
    /** R4 / t22: right-click handlers for the two dependency directions
     * (outgoing = this → picked, incoming = picked → this). When
     * either is present the row's context menu shows the "Add
     * dependency" sub-options. */
    onPickDependencyTarget?: (direction: 'outgoing' | 'incoming') => void;
    /** R4 / t22: true while the row's task is the source of an in-
     * progress "pick a dependency target" flow. */
    isPickSource?: boolean;
}

const STATUS_COLORS: Record<TaskStatus, string> = {
    TODO: 'bg-indigo-500/20 text-indigo-300',
    IN_PROGRESS: 'bg-blue-500/20 text-blue-300',
    IN_REVIEW: 'bg-purple-500/20 text-purple-300',
    DONE: 'bg-emerald-500/20 text-emerald-300',
    CANCELLED: 'bg-gray-500/20 text-gray-400',
};

function GanttRowImpl({
    task,
    barLeft,
    barWidth,
    timeScale,
    groupBy,
    canDrag,
    sprints,
    sprintFieldId,
    customFields,
    onClick,
    isPreviewing = false,
    onPickDependencyTarget,
    isPickSource = false,
}: GanttRowProps) {
    const { t } = useI18n();

    const range = resolveTaskRange(task);
    const statusClass = STATUS_COLORS[task.status] ?? STATUS_COLORS.TODO;
    const statusLabel = getStatusLabel(task.status, t);

    const sprintColour = getSprintColourForTask(
        task,
        sprintFieldId,
        sprints
    );

    // R1: tasks WITHOUT any dates also get a 12px marker dot pinned
    // at the timeline origin (left edge) so the user can still see
    // them in the timeline — they just don't have an x-position to
    // place a real bar. We only render this marker when the project
    // is actually showing the Gantt (which it always is in this
    // component) and the parent computed `barLeft=0` for them via
    // offsetForDate(null) in gantt-timeline.tsx.
    const isMarker =
        !range.hasAnyDate || (range.start == null || range.end == null || range.isPoint);
    const finalBarLeft = isMarker ? Math.max(0, barLeft - 6) : barLeft;
    const finalBarWidth = isMarker ? 12 : barWidth;

    // Date label for the left pane — only shows up when the bar has a
    // concrete range.
    let dateLabel = '';
    if (range.start && isValid(range.start) && range.end && isValid(range.end)) {
        if (range.isPoint) {
            dateLabel = format(range.start, 'MMM d');
        } else {
            dateLabel = `${format(range.start, 'MMM d')} → ${format(range.end, 'MMM d')}`;
        }
    }

    // t14: right-click → context menu (Add / Remove dependency).
    // Subtask rows inherit the same handler so users can wire
    // children up too. We only render the menu overlay when
    // contextMenu is non-null; otherwise nothing changes.
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
    const [addDepOpen, setAddDepOpen] = useState(false);
    const [removeDepOpen, setRemoveDepOpen] = useState(false);

    return (
        <div
            className={cn(
                'flex border-b border-white/5 hover:bg-white/5',
                isPreviewing ? 'bg-primary/5' : '',
                // R4 / t22: highlight the source task while the
                // user is in pick-target mode.
                isPickSource ? 'ring-2 ring-primary/60 bg-primary/10' : ''
            )}
            data-row-task-id={task.id}
            data-pick-source={isPickSource ? 'true' : undefined}
            onContextMenu={(e) => {
                if (!canDrag) return;
                e.preventDefault();
                e.stopPropagation();
                setContextMenu({ x: e.clientX, y: e.clientY });
            }}
        >
            {/* Left label pane — fixed width, scrolls vertically with
                the timeline but never horizontally. R9 / t36: no
                longer carries a chevron or subtask indent — every
                row is now a parent row (h-12). */}
            <div
                className="shrink-0 px-3 py-2 flex items-start gap-2 border-r border-white/5 bg-surface/30 w-64"
            >
                {/* R9 / t36: subtask expand chevron removed. */}
                <button
                    type="button"
                    onClick={() => onClick?.(task)}
                    className="flex-1 min-w-0 text-left group"
                >
                    <div
                        className={cn(
                            'truncate font-medium text-foreground group-hover:text-primary transition-colors',
                            'text-xs sm:text-sm',
                            task.status === 'DONE' ? 'line-through opacity-70' : ''
                        )}
                        title={task.title}
                    >
                        {task.title}
                        {/* R9 / t36: subtask-count badge removed. */}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                        <span
                            className={cn(
                                'inline-flex items-center px-1.5 py-0.5 rounded uppercase tracking-wide font-medium',
                                'text-[10px]',
                                statusClass
                            )}
                        >
                            {statusLabel}
                        </span>
                        {dateLabel && (
                            <span className="text-foreground-muted text-[10px]">
                                {dateLabel}
                            </span>
                        )}
                        {sprintColour && (
                            <span
                                className="inline-block h-2 w-2 rounded-full"
                                style={{ backgroundColor: sprintColour }}
                                aria-hidden
                            />
                        )}
                    </div>
                    {task.assignees && task.assignees.length > 0 && (
                        <div className="mt-1 flex items-center -space-x-1">
                            {task.assignees.slice(0, 3).map((a) => (
                                <div
                                    key={a.userId}
                                    className="h-4 w-4 rounded-full bg-primary text-primary-foreground text-[8px] font-bold flex items-center justify-center ring-1 ring-surface"
                                    title={a.user?.name || a.userId}
                                >
                                    {a.user?.name?.[0]?.toUpperCase() ?? '?'}
                                </div>
                            ))}
                            {task.assignees.length > 3 && (
                                <div className="h-4 w-4 rounded-full bg-surface-hover text-foreground-muted text-[8px] font-bold flex items-center justify-center ring-1 ring-surface">
                                    +{task.assignees.length - 3}
                                </div>
                            )}
                        </div>
                    )}
                </button>
            </div>

            {/* Right timeline pane — the bar floats above the grid.
                overflow-hidden clips bars that start before the visible
                origin (negative left) so they don't bleed into the
                label pane; the user can pan prev to bring them into
                view. R9 / t36: every row is h-12 now (no h-9 subtask
                variant). */}
            <div
                className="relative flex-1 overflow-hidden h-12"
            >
                <GanttBar
                    task={task}
                    left={finalBarLeft}
                    width={finalBarWidth}
                    timeScaleLabel={timeScale}
                    canDrag={canDrag}
                    onClick={onClick}
                    isPreviewing={isPreviewing}
                />
            </div>

            {/* R6 / t31: right-click context menu. The picker has a
                single "Add a dependency on this task…" option
                (no direction picker) when onPickDependencyTarget
                is provided; falls back to the legacy dialog for
                read-only contexts where the picker is disabled. */}
            <DependencyContextMenu
                anchor={contextMenu}
                onClose={() => setContextMenu(null)}
                onAddDependency={
                    onPickDependencyTarget
                        ? () => {
                              setContextMenu(null);
                              // R6: direction is irrelevant — every
                              // dep is FS. We still call the
                              // signature-compatible picker so the
                              // parent enters pick-target mode.
                              onPickDependencyTarget('outgoing');
                          }
                        : undefined
                }
                onAddDialog={
                    !onPickDependencyTarget
                        ? () => {
                              setContextMenu(null);
                              setAddDepOpen(true);
                          }
                        : undefined
                }
                onRemove={() => {
                    setContextMenu(null);
                    setRemoveDepOpen(true);
                }}
            />
            <AddDependencyDialog
                open={addDepOpen}
                onOpenChange={setAddDepOpen}
                task={task}
            />
            <RemoveDependencyDialog
                open={removeDepOpen}
                onOpenChange={setRemoveDepOpen}
                task={task}
            />
        </div>
    );
}

// ============================================
// t14: dependency context menu (small popover)
// ============================================
//
// Positioned at the click coordinate. Closes on outside click or
// Escape. Disabled while the underlying mutation is in-flight to
// avoid double-submits.
//
// R4 / t22: when the parent GanttView hands down
// `onPickDependencyTarget`, the menu shows two "add dep"
// direction options that put the view into a cursor-driven
// "pick a target" mode instead of opening the dialog. The
// legacy dialog is preserved as a fallback for read-only
// contexts where onPickDependencyTarget isn't available.

function DependencyContextMenu({
    anchor,
    onClose,
    onAddDependency,
    onAddDialog,
    onRemove,
}: {
    anchor: { x: number; y: number } | null;
    onClose: () => void;
    onAddDependency?: () => void;
    onAddDialog?: () => void;
    onRemove: () => void;
}) {
    const { t } = useI18n();
    if (!anchor) return null;
    return (
        <>
            {/* Full-screen backdrop to dismiss on outside click. */}
            <div
                className="fixed inset-0 z-40"
                onClick={onClose}
                onContextMenu={(e) => {
                    e.preventDefault();
                    onClose();
                }}
            />
            <div
                className="fixed z-50 min-w-[260px] rounded-md border border-border bg-surface shadow-lg py-1 text-sm"
                style={{ left: anchor.x, top: anchor.y }}
                role="menu"
            >
                {onAddDependency && (
                    <button
                        type="button"
                        onClick={onAddDependency}
                        className="w-full text-left px-3 py-2 hover:bg-surface-hover flex items-center gap-2 cursor-pointer"
                        role="menuitem"
                        data-testid="gantt-dep-add"
                    >
                        <Link2 className="h-4 w-4 text-foreground-muted" />
                        {t('gantt.depMenuSingle')}
                    </button>
                )}
                {onAddDialog && (
                    <button
                        type="button"
                        onClick={onAddDialog}
                        className="w-full text-left px-3 py-2 hover:bg-surface-hover flex items-center gap-2 cursor-pointer"
                        role="menuitem"
                        data-testid="gantt-dep-add-dialog"
                    >
                        <Plus className="h-4 w-4 text-foreground-muted" />
                        {t('gantt.addDependency')}
                    </button>
                )}
                <button
                    type="button"
                    onClick={onRemove}
                    className="w-full text-left px-3 py-2 hover:bg-surface-hover flex items-center gap-2 cursor-pointer"
                    role="menuitem"
                    data-testid="gantt-dep-remove"
                >
                    <Trash2 className="h-4 w-4 text-foreground-muted" />
                    {t('gantt.removeDependency')}
                </button>
            </div>
        </>
    );
}

// ============================================
// t14: add-dependency dialog
// ============================================
//
// Three states:
//   - Direction picker (this task as successor vs as predecessor).
//   - Target picker (search the project's other tasks).
//   - Type picker (FS / SS / FF / SF; default FS).
//
// On submit, POSTs /task-dependencies and closes.

function AddDependencyDialog({
    open,
    onOpenChange,
    task,
}: {
    open: boolean;
    onOpenChange: (next: boolean) => void;
    task: Task;
}) {
    const { t } = useI18n();
    const { data: dependenciesData } = useTaskDependencies(task.projectId);
    const { data: projectTasksData } = useTasks(task.projectId);
    const createDependency = useCreateTaskDependency(task.projectId);

    // Step 1 — pick direction. The picked side determines whether
    // this task is the predecessor or the successor of the new edge.
    const [direction, setDirection] = useState<'successor' | 'predecessor'>('successor');
    const [targetId, setTargetId] = useState<string | null>(null);
    const [type, setType] = useState<TaskDependencyType>('FS');
    const [search, setSearch] = useState('');

    const projectTasks = projectTasksData?.tasks || [];
    const deps = dependenciesData?.dependencies || [];
    const q = search.trim().toLowerCase();
    const candidates = projectTasks.filter((candidate) => {
        if (candidate.id === task.id) return false;
        if (q !== '' && !candidate.title.toLowerCase().includes(q)) return false;
        // Avoid duplicates in the chosen direction.
        const alreadyEdge = deps.some((d) =>
            direction === 'successor'
                ? d.predecessorId === candidate.id && d.successorId === task.id
                : d.successorId === candidate.id && d.predecessorId === task.id
        );
        if (alreadyEdge) return false;
        return true;
    });

    const handleSubmit = async () => {
        if (!targetId) return;
        await createDependency.mutateAsync({
            predecessorId: direction === 'successor' ? targetId : task.id,
            successorId: direction === 'successor' ? task.id : targetId,
            type,
        });
        setTargetId(null);
        setType('FS');
        setSearch('');
        onOpenChange(false);
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) {
                    setTargetId(null);
                    setType('FS');
                    setSearch('');
                    setDirection('successor');
                }
                onOpenChange(next);
            }}
        >
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('gantt.addDependencyTitle')}</DialogTitle>
                    <DialogDescription>
                        {t('gantt.addDependencyDescription')}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    {/* Direction */}
                    <div className="space-y-1.5">
                        <label className="text-xs text-foreground-muted font-medium">
                            {t('gantt.dependencyDirection')}
                        </label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setDirection('successor')}
                                className={cn(
                                    'flex-1 px-3 py-2 text-sm rounded-md border',
                                    direction === 'successor'
                                        ? 'bg-primary/10 border-primary text-primary'
                                        : 'border-white/10 hover:bg-surface-hover'
                                )}
                            >
                                {t('gantt.dependencyAsSuccessor')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setDirection('predecessor')}
                                className={cn(
                                    'flex-1 px-3 py-2 text-sm rounded-md border',
                                    direction === 'predecessor'
                                        ? 'bg-primary/10 border-primary text-primary'
                                        : 'border-white/10 hover:bg-surface-hover'
                                )}
                            >
                                {t('gantt.dependencyAsPredecessor')}
                            </button>
                        </div>
                    </div>

                    {/* Target */}
                    <div className="space-y-1.5">
                        <label className="text-xs text-foreground-muted font-medium">
                            {t('gantt.dependencyTarget')}
                        </label>
                        <Input
                            placeholder={t('gantt.dependencySearchPlaceholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="h-8 text-sm"
                        />
                        <div className="max-h-44 overflow-y-auto rounded-md border border-white/10">
                            {candidates.length === 0 ? (
                                <p className="text-xs text-foreground-muted px-2 py-2">
                                    {q === ''
                                        ? t('tasks.dependsOnEmpty')
                                        : t('tasks.dependsOnNoMatches')}
                                </p>
                            ) : (
                                candidates.slice(0, 50).map((candidate) => (
                                    <button
                                        key={candidate.id}
                                        type="button"
                                        onClick={() => setTargetId(candidate.id)}
                                        className={cn(
                                            'w-full text-left px-2 py-1.5 text-sm hover:bg-surface-hover flex items-center gap-2',
                                            targetId === candidate.id && 'bg-surface-hover'
                                        )}
                                    >
                                        <span className="truncate flex-1">{candidate.title}</span>
                                        <Badge variant="secondary" className="text-[10px] shrink-0">
                                            {candidate.status}
                                        </Badge>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Type */}
                    <div className="space-y-1.5">
                        <label className="text-xs text-foreground-muted font-medium">
                            {t('gantt.dependencyType')}
                        </label>
                        <div className="grid grid-cols-4 gap-2">
                            {(['FS', 'SS', 'FF', 'SF'] as TaskDependencyType[]).map((tt) => (
                                <button
                                    key={tt}
                                    type="button"
                                    onClick={() => setType(tt)}
                                    className={cn(
                                        'px-2 py-1.5 text-xs rounded-md border',
                                        type === tt
                                            ? 'bg-primary/10 border-primary text-primary'
                                            : 'border-white/10 hover:bg-surface-hover'
                                    )}
                                    title={
                                        tt === 'FS' ? t('gantt.dependencyTypeFS')
                                        : tt === 'SS' ? t('gantt.dependencyTypeSS')
                                        : tt === 'FF' ? t('gantt.dependencyTypeFF')
                                        : t('gantt.dependencyTypeSF')
                                    }
                                >
                                    {tt}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!targetId || createDependency.isPending}
                    >
                        {createDependency.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Link2 className="h-4 w-4" />
                        )}
                        {t('common.add')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ============================================
// t14: remove-dependency dialog
// ============================================
//
// Lists every dependency edge touching this task (both directions).
// Each row has an × button that DELETEs the edge.

function RemoveDependencyDialog({
    open,
    onOpenChange,
    task,
}: {
    open: boolean;
    onOpenChange: (next: boolean) => void;
    task: Task;
}) {
    const { t } = useI18n();
    const { data: dependenciesData } = useTaskDependencies(task.projectId);
    const { data: projectTasksData } = useTasks(task.projectId);
    const deleteDependency = useDeleteTaskDependency(task.projectId);

    const deps = dependenciesData?.dependencies || [];
    const myDeps = deps.filter(
        (d) => d.predecessorId === task.id || d.successorId === task.id
    );
    const projectTasks = projectTasksData?.tasks || [];
    const taskById = new Map(projectTasks.map((p) => [p.id, p]));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('gantt.removeDependencyTitle')}</DialogTitle>
                    <DialogDescription>
                        {t('gantt.removeDependencyDescription')}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 py-2 max-h-72 overflow-y-auto">
                    {myDeps.length === 0 ? (
                        <p className="text-xs text-foreground-muted px-2 py-2">
                            {t('tasks.dependsOnEmpty')}
                        </p>
                    ) : (
                        myDeps.map((d) => {
                            const isOutgoing = d.predecessorId === task.id;
                            const other = taskById.get(
                                isOutgoing ? d.successorId : d.predecessorId
                            );
                            return (
                                <div
                                    key={d.id}
                                    className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md border border-white/10"
                                >
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <Badge
                                            variant="secondary"
                                            className="text-xs shrink-0"
                                            title={`Dependency type: ${d.type}`}
                                        >
                                            {d.type}
                                        </Badge>
                                        <span className="text-xs text-foreground-muted shrink-0">
                                            {isOutgoing ? '→' : '←'}
                                        </span>
                                        <span className="text-sm truncate">
                                            {other?.title ||
                                                (isOutgoing ? d.successorId : d.predecessorId)}
                                        </span>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
                                        onClick={() => deleteDependency.mutate(d.id)}
                                        disabled={deleteDependency.isPending}
                                        title={t('common.delete')}
                                    >
                                        <XIcon className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            );
                        })
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export const GanttRow = memo(GanttRowImpl);
